/**
 * DashboardHeader Component (NavBar)
 *
 * Header component for dashboard pages. Includes:
 * - The ☰ that opens the side panel, below 1024px
 * - Page title
 * - Header menu (support, notifications, profile, etc.)
 * - Sticky header behavior on scroll
 *
 * The ☰ lives here rather than floating over the page (Tyler, 2026-10-09:
 * nothing floats over the bottom of a page). It reads the same sidebar
 * context as the drawer, so it shows at the widths the drawer serves.
 *
 * @param {Object} props
 * @param {string} props.title - Page title to display
 */
import { useEffect, useRef, useCallback } from "react";
import { Menu } from "lucide-react";
import HeaderMenu from "@/component/inc/headerMenu";
import { useSidebarContext } from "@common/layout/sidebar/SidebarProvider";
import { useHeaderHeight } from "@/hooks/useHeaderHeight";

const NavBar = ({ title }) => {
  const headerRef = useRef(null);
  const { isSmallScreen, openMobileDrawer } = useSidebarContext();

  /* Method that will fix header after a specific scrollable */
  const isSticky = useCallback(() => {
    const header = headerRef.current || document.querySelector(".topheader");
    if (!header) return;

    const scrollTop = window.scrollY;
    if (scrollTop >= 25) {
      header.classList.add("is-sticky");
    } else {
      header.classList.remove("is-sticky");
    }
  }, []);

  // Sticky Menu Area
  useEffect(() => {
    window.addEventListener("scroll", isSticky);
    return () => {
      window.removeEventListener("scroll", isSticky);
    };
  }, [isSticky]);

  // The header is fixed, so content offsets by its height.
  useHeaderHeight(headerRef);

  return (
    <div ref={headerRef} className="navbartest topheader gradient_header">
      <div className="container-fluid">
        <div className="topheader_flex justify-content-between">
          <div className="d-flex align-items-center">
            {isSmallScreen && (
              <button
                type="button"
                className="headerMenuButton"
                onClick={openMobileDrawer}
                aria-label="Open navigation menu"
                data-testid="header-nav-menu"
              >
                <Menu aria-hidden="true" />
              </button>
            )}
            <h1 className="headerTitle_block mb-0">{title}</h1>
          </div>
          <HeaderMenu dashboard={true} />
        </div>
      </div>
    </div>
  );
};

export default NavBar;
